import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import Table from "react-bootstrap/Table";
import { Spinner } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faFileExcel } from "@fortawesome/free-solid-svg-icons";
import styles from "../FastTable/FastTable.module.scss";
import ServerPagination from "./ServerPagination";
import FastFilter from "../FastTable/FastFilter";
import { createExcelFile } from "../../utils/Excel";
import { TABLE_TYPES, VARIOUS_VALUES } from "../FastTable/FastTable";
import getContrastColor from "../../utils/getContrastColor";

export default function ServerTable({
  title = "",
  data = [],
  uniqueKey = "id",
  isLoading,
  isError,
  columns = [],
  showFilters = false,
  showExcel = true,
  showPagination = true,
  page = 1,
  pageSize = 10,
  totalCount = 0,
  sortBy = "",
  sortDir = "asc",
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onFiltersChange,
  updateSearchParams = false,
  onSelectRow,
  selected,
  SelectIcon = () => <FontAwesomeIcon icon={faChevronRight} />,
  headerLeft = null,
  headerCenter = null,
  headerRight = null,
  emptyMessage = "Sin datos",
  cellStyle = () => ({}),
}) {
  const parsedColumns = useMemo(() => {
    if (Array.isArray(data) && data.length > 0) {
      if (columns.length === 0) {
        return Object.keys(data[0]).map((key) => ({
          key,
          label: key,
        }));
      }
    }
    if (Array.isArray(columns) && columns.length > 0) {
      return columns;
    }
    return [];
  }, [data, columns]);

  const columnsForFilter = useMemo(
    () => parsedColumns.filter((col) => col.filter),
    [parsedColumns]
  );

  const [filters, setFilters] = useState([]);

  useEffect(() => {
    if (typeof onFiltersChange === "function") {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange]);

  const extraColumn = useMemo(
    () => (typeof onSelectRow === "function" ? 1 : 0),
    [onSelectRow]
  );

  const handleCreateExcelFile = () => {
    const fileName =
      title.replace(/\s/g, "_").toLowerCase() +
      "-" +
      Math.floor(Date.now() / 1000);
    createExcelFile(
      data.map((row) => {
        return parsedColumns.reduce((acc, col) => {
          const key = col?.sourceKey ? col.sourceKey : col.key;
          acc[col.label] =
            typeof row[key] !== "undefined" ? renderCellString(col, row) : "";
          return acc;
        }, {});
      }),
      `${fileName}.xlsx`
    );
  };

  const shouldShowHeaderRow =
    showFilters || showExcel || headerLeft || headerCenter || headerRight;

  const leftContent =
    headerLeft ||
    (showFilters &&
      typeof onFiltersChange === "function" &&
      columnsForFilter.length > 0 && (
        <FastFilter
          data={data}
          setFilters={setFilters}
          columns={columnsForFilter}
          updateSearchParams={updateSearchParams}
        />
      ));

  return (
    <Container className={styles.FastTable} fluid={true}>
      {title && title !== "" && <h2>{title}</h2>}
      {shouldShowHeaderRow && (
        <Row className="mb-4">
          <Col>{leftContent}</Col>
          <Col>{headerCenter}</Col>
          <Col className={styles.TableHeader}>
            {headerRight}
            {showExcel && (
              <Button
                variant="secondary"
                style={{ fontSize: 14, fontWeight: 600 }}
                onClick={handleCreateExcelFile}
              >
                Exportar a Excel{" "}
                <FontAwesomeIcon icon={faFileExcel} className="ms-2" />
              </Button>
            )}
          </Col>
        </Row>
      )}
      <Row>
        <Col className={`table-container ${styles.TableContainer}`}>
          {isLoading && <Spinner />}
          {isError && <Alert variant="danger">Error</Alert>}
          <Table className="table-hover">
            <thead>
              <tr>
                {parsedColumns.map(
                  (col, index) =>
                    col?.hidden !== true && (
                      <th key={index} className="text-center">
                        <div>
                          <span>{col.label}</span>
                          {col.sortable !== false &&
                            typeof onSortChange === "function" && (
                              <SortButton
                                sortKey={col.sortKey || col.key}
                                sortBy={sortBy}
                                sortDir={sortDir}
                                onSortChange={onSortChange}
                              />
                            )}
                        </div>
                      </th>
                    )
                )}
                {extraColumn > 0 && (
                  <th
                    style={{
                      width: extraColumn * 33,
                    }}
                  />
                )}
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data) && data.length > 0 ? (
                data.map((row, rowIndex) => (
                  <tr
                    key={row?.[uniqueKey] ?? rowIndex}
                    className={[
                      typeof selected === "function" && selected(row)
                        ? styles.SelectedRow
                        : "",
                      typeof onSelectRow === "function"
                        ? styles.SelectableRow
                        : "",
                    ].join(" ")}
                    onClick={() => {
                      if (typeof onSelectRow === "function") {
                        onSelectRow(row);
                      }
                    }}
                  >
                    {parsedColumns.map((col, colIndex) => {
                      const key = col.key;
                      const value = row[key];
                      if (col?.hidden === true) return null;
                      return (
                        <Cell
                          key={colIndex}
                          col={col}
                          value={value}
                          row={row}
                          style={cellStyle(row, col)}
                        />
                      );
                    })}
                    {extraColumn > 0 && (
                      <td className={styles.ActionsCell}>
                        <div>
                          {typeof onSelectRow === "function" && (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                onSelectRow(row);
                              }}
                              size="sm"
                            >
                              <SelectIcon />
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                !isLoading && (
                  <tr>
                    <td
                      colSpan={Math.max(parsedColumns.length + extraColumn, 1)}
                      className="text-center"
                    >
                      {emptyMessage}
                    </td>
                  </tr>
                )
              )}
              {showPagination && (
                <tr>
                  <td
                    colSpan={Math.max(parsedColumns.length + extraColumn, 1)}
                  >
                    <ServerPagination
                      page={page}
                      pageSize={pageSize}
                      totalCount={totalCount}
                      onPageChange={onPageChange}
                      onPageSizeChange={onPageSizeChange}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}

const renderCellString = (col, row) => {
  const key = col?.sourceKey ? col.sourceKey : col.key;
  if (typeof col?.render === "function") {
    const rendered = col.render(row);
    if (typeof rendered === "string") return rendered;
  }
  if (typeof row[key] === "undefined") return "";
  return col.type === TABLE_TYPES.date
    ? new Date(row[key]).toLocaleString()
    : col.type === TABLE_TYPES.dateUnix
    ? col?.hideDateZero === true && parseInt(row[key], 10) === 0
      ? ""
      : new Date(parseInt(row[key], 10) * 1000).toLocaleString()
    : col.type === TABLE_TYPES.dateUnixDate
    ? new Date(parseInt(row[key], 10) * 1000).toLocaleDateString()
    : col.type === TABLE_TYPES.dateUnixTime
    ? new Date(parseInt(row[key], 10) * 1000).toLocaleTimeString()
    : col.type === TABLE_TYPES.boolean
    ? row[key]
      ? "Sí"
      : "No"
    : row[key];
};

const Cell = ({ row, col, value, style }) => {
  const _style = useMemo(() => {
    const __style = {};
    if (col?.type === TABLE_TYPES.color) {
      __style.backgroundColor = value;
      __style.color = getContrastColor(value);
      __style.fontWeight = 700;
      __style.fontFamily = "monospace";
      __style.textTransform = "uppercase";
      __style.letterSpacing = "1px";
      __style.fontSize = 12;
    }
    return { ...__style, ...col?.style, ...style };
  }, [col?.style, col?.type, style, value]);

  return (
    <td
      className={
        col?.type === TABLE_TYPES.string || col?.type === TABLE_TYPES.options
          ? "text-start"
          : col?.type === TABLE_TYPES.image ||
            col?.type === TABLE_TYPES.color ||
            col?.style?.textAlign === "center"
          ? "text-center"
          : "text-end"
      }
      style={_style}
      title={JSON.stringify(value)}
    >
      {col.prefix && <Prefix prefix={col.prefix} row={row} />}
      {typeof col?.render === "function" && row ? (
        col.render(row)
      ) : col?.type === TABLE_TYPES.currency ? (
        <>{!isNaN(value) ? parseFloat(value).toLocaleString() : value}</>
      ) : col?.type === TABLE_TYPES.select ? (
        <>{col?.options.find((option) => option.value === value)?.label}</>
      ) : value !== "null" ? (
        value === VARIOUS_VALUES ? (
          VARIOUS_VALUES
        ) : col.type === TABLE_TYPES.image && value !== "" ? (
          <img src={value} alt="content" {...col.attr} />
        ) : (
          renderCellString(col, row)
        )
      ) : (
        ""
      )}
      {col.suffix && <span> {col.suffix}</span>}
    </td>
  );
};

const SortButton = ({ sortKey, sortBy, sortDir, onSortChange }) => {
  const handleClick = () => {
    if (typeof onSortChange !== "function") return;
    if (sortBy === sortKey) {
      onSortChange({
        sortBy: sortKey,
        sortDir: sortDir === "asc" ? "desc" : "asc",
      });
    } else {
      onSortChange({ sortBy: sortKey, sortDir: "asc" });
    }
  };

  const activeUp = sortBy === sortKey && sortDir === "asc";
  const activeDown = sortBy === sortKey && sortDir === "desc";

  return (
    <button onClick={handleClick} className={styles.SortButton}>
      <svg
        version="1.1"
        x="0px"
        y="0px"
        viewBox="0 0 10 8.6"
        style={{ enableBackground: "new 0 0 10 8.6" }}
        className={[
          styles.SortButtonUp,
          activeUp ? styles.SortButtonActive : "inactive",
        ].join(" ")}
      >
        <polygon points="5,0 0,8.6 10,8.6" />
      </svg>
      <svg
        version="1.1"
        x="0px"
        y="0px"
        viewBox="0 0 10 8.6"
        style={{ enableBackground: "new 0 0 10 8.6" }}
        className={[
          styles.SortButtonDown,
          activeDown ? styles.SortButtonActive : "inactive",
        ].join(" ")}
      >
        <polygon points="5,8.6 0,0 10,0" />
      </svg>
    </button>
  );
};

function Prefix({ prefix, row }) {
  return <span>{typeof prefix === "function" ? prefix(row) : prefix}</span>;
}
